import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Search, SlidersHorizontal, Users, Baby, ArrowRight, Loader2, Compass } from "lucide-react";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Sections5";
import { WhatsAppFloat, MobileStickyCTA } from "@/components/landing/Conversion";
import { PackageCard } from "@/components/packages/PackageCard";
import { CompareBar } from "@/components/packages/CompareBar";
import { fetchPackages, fetchPackageFacets, getTravellers, setTravellers } from "@/lib/api";

const DURATIONS = [
    { key: "1", label: "1 Day" }, { key: "2", label: "2 Days" }, { key: "3", label: "3 Days" },
    { key: "4", label: "4 Days" }, { key: "5", label: "5 Days" }, { key: "6-7", label: "6–7 Days" }, { key: "8+", label: "8+ Days" },
];
const BUDGETS = [
    { v: "", label: "Any budget" }, { v: "5000", label: "Under ₹5,000" }, { v: "10000", label: "Under ₹10,000" },
    { v: "15000", label: "Under ₹15,000" }, { v: "20000", label: "Under ₹20,000" }, { v: "30000", label: "Under ₹30,000" }, { v: "50000", label: "Under ₹50,000" },
];
const GROUP_TYPES = [
    { v: "", label: "Any group" }, { v: "school", label: "School" }, { v: "college", label: "College" },
    { v: "corporate", label: "Corporate" }, { v: "family", label: "Family" }, { v: "group", label: "Friends / Group" },
];

const EMPTY = { search: "", category: "", duration: "", budget_max: "", hotel: "", transport: "", meals: "", group_type: "", depart_city: "", depart_date: "" };

export default function PackagesPage() {
    const { destSlug } = useParams();
    const [filters, setFilters] = useState(EMPTY);
    const [sort, setSort] = useState("recommended");
    const [trav, setTrav] = useState(() => getTravellers());
    const [facets, setFacets] = useState(null);
    const [data, setData] = useState({ packages: [], count: 0 });
    const [loading, setLoading] = useState(true);
    const [showFilters, setShowFilters] = useState(false);

    useEffect(() => { fetchPackageFacets().then(setFacets).catch(() => {}); }, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { sort };
            if (destSlug) params.destination = destSlug;
            for (const [k, v] of Object.entries(filters)) {
                if (!v || k === "depart_date") continue;
                params[k === "budget_max" ? "budget_max" : k] = v;
            }
            const res = await fetchPackages(params);
            let list = res.packages;
            if (filters.depart_date) {
                list = list.filter((p) => (p.departures || []).length === 0 || p.departures.some((d) => d.date === filters.depart_date));
            }
            setData({ packages: list, count: list.length });
        } catch {
            setData({ packages: [], count: 0 });
        } finally {
            setLoading(false);
        }
    }, [filters, sort, destSlug]);

    useEffect(() => {
        const t = setTimeout(load, filters.search ? 350 : 0);
        return () => clearTimeout(t);
    }, [load, filters.search]);

    useEffect(() => {
        document.title = destSlug ? `${destSlug.replace(/-/g, " ")} Tour Packages | GhumneChaley.com` : "Tour Packages — Compare & Build Your Trip | GhumneChaley.com";
        document.querySelector('meta[name="description"]')?.setAttribute("content",
            "Compare GhumneChaley tour packages, explore day-wise itineraries and know your trip cost before you book. School, college, corporate and group tours across India.");
    }, [destSlug]);

    const updateTrav = (k, v) => {
        const next = { ...trav, [k]: Math.max(k === "adults" ? 1 : 0, v) };
        setTrav(next);
        setTravellers(next);
    };

    const hasPopularity = useMemo(() => data.packages.some((p) => p.popularity_score != null), [data.packages]);
    const sel = "w-full rounded-lg border border-stone-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-saffron";
    const lab = "block text-[11px] font-bold uppercase tracking-wider text-stone-500 mb-1";
    const set = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));
    const destName = destSlug ? (data.packages[0]?.destination_name || destSlug.replace(/-/g, " ")) : null;

    return (
        <div className="bg-cream min-h-screen pb-14 md:pb-0" data-testid="packages-page">
            <Header />

            <section className="bg-navy text-white py-16 sm:py-20" data-testid="packages-hero">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <p className="font-script text-3xl text-gold -rotate-1 inline-block">har safar, ek kahani</p>
                    <h1 className="mt-2 font-display font-black text-4xl sm:text-5xl tracking-tight" data-testid="packages-h1">
                        {destName ? <>{destName} <span className="text-saffron">Packages.</span></> : <>Find Your <span className="text-saffron">Perfect Getaway.</span></>}
                    </h1>
                    <p className="mt-4 text-white/70 max-w-2xl leading-relaxed">
                        Compare tour packages, explore your itinerary and know what your journey will cost before you book.
                    </p>
                    <div className="mt-6 flex flex-wrap gap-3">
                        <a href="#packages-grid" data-testid="explore-packages-btn" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-saffron hover:bg-saffron-dark text-sm font-display font-black uppercase tracking-wider transition-colors">
                            Explore Packages <ArrowRight className="h-4 w-4" />
                        </a>
                        <Link to="/plan-my-trip" data-testid="build-custom-trip-btn" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border-2 border-white/60 text-sm font-display font-black uppercase tracking-wider hover:bg-white hover:text-navy transition-all">
                            <Compass className="h-4 w-4" /> Build My Custom Trip
                        </Link>
                    </div>
                </div>
            </section>

            <section id="packages-grid" className="py-10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="bg-white rounded-2xl border border-stone-300 p-5" data-testid="filters-panel">
                        <div className="flex flex-wrap items-center gap-3">
                            <div className="relative flex-1 min-w-[220px]">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
                                <input data-testid="filter-search" className={`${sel} pl-9`} placeholder="Search Manali, Goa, school trip…" value={filters.search} onChange={set("search")} />
                            </div>
                            <button onClick={() => setShowFilters((s) => !s)} data-testid="filters-toggle" className="lg:hidden inline-flex items-center gap-1.5 rounded-full border border-stone-300 px-4 py-2.5 text-xs font-black uppercase tracking-wider text-navy">
                                <SlidersHorizontal className="h-3.5 w-3.5" /> Filters
                            </button>
                        </div>

                        <div className={`mt-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 ${showFilters ? "" : "hidden lg:grid"}`} data-testid="filters-grid">
                            <div>
                                <label className={lab}>Trip category</label>
                                <select data-testid="filter-category" className={sel} value={filters.category} onChange={set("category")}>
                                    <option value="">All categories</option>
                                    {(facets?.categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className={lab}>Budget per person</label>
                                <select data-testid="filter-budget" className={sel} value={filters.budget_max} onChange={set("budget_max")}>
                                    {BUDGETS.map((b) => <option key={b.v} value={b.v}>{b.label}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className={lab}>Hotel category</label>
                                <select data-testid="filter-hotel" className={sel} value={filters.hotel} onChange={set("hotel")}>
                                    <option value="">Any stay</option>
                                    {(facets?.hotels || []).map((c) => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className={lab}>Transport</label>
                                <select data-testid="filter-transport" className={sel} value={filters.transport} onChange={set("transport")}>
                                    <option value="">Any transport</option>
                                    {(facets?.transports || []).map((c) => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className={lab}>Meals</label>
                                <select data-testid="filter-meals" className={sel} value={filters.meals} onChange={set("meals")}>
                                    <option value="">Any meal plan</option>
                                    {(facets?.meals || []).map((c) => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className={lab}>Group type</label>
                                <select data-testid="filter-group-type" className={sel} value={filters.group_type} onChange={set("group_type")}>
                                    {GROUP_TYPES.map((g) => <option key={g.v} value={g.v}>{g.label}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className={lab}>Departure city</label>
                                <select data-testid="filter-depart-city" className={sel} value={filters.depart_city} onChange={set("depart_city")}>
                                    <option value="">Any city</option>
                                    {(facets?.departure_cities || []).map((c) => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className={lab}>Departure date</label>
                                <input type="date" data-testid="filter-depart-date" className={sel} value={filters.depart_date} onChange={set("depart_date")} />
                            </div>
                        </div>

                        <div className="mt-4 pt-4 border-t border-stone-200 flex flex-wrap items-center gap-x-6 gap-y-3">
                            <div>
                                <span className={lab}>Duration</span>
                                <div className="flex flex-wrap gap-1.5" data-testid="filter-duration">
                                    {DURATIONS.map((d) => (
                                        <button key={d.key} onClick={() => setFilters((f) => ({ ...f, duration: f.duration === d.key ? "" : d.key }))}
                                            data-testid={`duration-${d.key}`}
                                            className={`rounded-full px-3.5 py-1.5 text-[11px] font-black uppercase tracking-wider transition-colors ${filters.duration === d.key ? "bg-navy text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"}`}>
                                            {d.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="flex items-end gap-5 ml-auto">
                                <div>
                                    <span className={lab}><Users className="inline h-3 w-3 mr-1" />Adults</span>
                                    <div className="flex items-center gap-2">
                                        <button onClick={() => updateTrav("adults", trav.adults - 1)} data-testid="adults-minus" className="h-8 w-8 rounded-full border border-stone-300 font-black text-navy hover:border-saffron">−</button>
                                        <span className="font-display font-black text-navy w-5 text-center" data-testid="adults-count">{trav.adults}</span>
                                        <button onClick={() => updateTrav("adults", trav.adults + 1)} data-testid="adults-plus" className="h-8 w-8 rounded-full border border-stone-300 font-black text-navy hover:border-saffron">+</button>
                                    </div>
                                </div>
                                <div>
                                    <span className={lab}><Baby className="inline h-3 w-3 mr-1" />Children</span>
                                    <div className="flex items-center gap-2">
                                        <button onClick={() => updateTrav("children", trav.children - 1)} data-testid="children-minus" className="h-8 w-8 rounded-full border border-stone-300 font-black text-navy hover:border-saffron">−</button>
                                        <span className="font-display font-black text-navy w-5 text-center" data-testid="children-count">{trav.children}</span>
                                        <button onClick={() => updateTrav("children", trav.children + 1)} data-testid="children-plus" className="h-8 w-8 rounded-full border border-stone-300 font-black text-navy hover:border-saffron">+</button>
                                    </div>
                                </div>
                                <div>
                                    <span className={lab}>Sort by</span>
                                    <select data-testid="sort-select" className={sel} value={sort} onChange={(e) => setSort(e.target.value)}>
                                        <option value="recommended">Recommended</option>
                                        <option value="price_low">Price: low to high</option>
                                        <option value="price_high">Price: high to low</option>
                                        <option value="duration">Duration</option>
                                        {hasPopularity && <option value="popularity">Popularity</option>}
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="mt-6 flex items-center justify-between">
                        <p className="text-sm font-bold text-stone-500" data-testid="results-count">{loading ? "Searching…" : `${data.count} package${data.count === 1 ? "" : "s"} found`}</p>
                        {(Object.values(filters).some(Boolean)) && (
                            <button onClick={() => setFilters(EMPTY)} data-testid="filters-clear" className="text-xs font-black uppercase tracking-wider text-saffron hover:text-saffron-dark">Clear filters</button>
                        )}
                    </div>

                    {loading ? (
                        <div className="py-20 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-saffron" /></div>
                    ) : data.count === 0 ? (
                        <div className="py-20 text-center" data-testid="packages-empty">
                            <p className="font-display font-black text-2xl text-navy">No packages match those filters.</p>
                            <p className="mt-2 text-sm text-stone-500">Loosen a filter — or let us build this exact trip for you.</p>
                            <Link to="/plan-my-trip" data-testid="empty-custom-trip-btn" className="mt-6 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-saffron hover:bg-saffron-dark text-white text-sm font-display font-black uppercase tracking-wider transition-colors">Build My Custom Trip <ArrowRight className="h-4 w-4" /></Link>
                        </div>
                    ) : (
                        <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="packages-grid-list">
                            {data.packages.map((p) => <PackageCard key={p.id} pkg={p} />)}
                        </div>
                    )}
                </div>
            </section>

            <CompareBar />
            <Footer />
            <WhatsAppFloat /><MobileStickyCTA />
        </div>
    );
}
